/* WorkflowPipeline — read-side visual for a workflow doc as a TRUE
 * top-to-bottom flow graph: START (name + inputs) → step nodes → END
 * (output keys). Branches fork through a diamond-ish decision into
 * labeled then/else lanes that rejoin; loops are framed "For each"
 * containers; compensation hangs off a dashed red edge. Synthesis lives
 * in workflowGraph.ts, wording in workflowText.ts, edge geometry in
 * edgeRouting.ts (dir 'v'). Nodes are DRAGGABLE (view-only — positions
 * live in local state, the document is never written). */
import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    AlertTriangle,
    Repeat
} from 'lucide-react';
import {
    useAppStudio
} from '../../AppStudioContext';
import {
    buildWorkflowGraph,
    frameRect
} from './workflowGraph';
import {
    GEdge,
    routeEdges
} from './edgeRouting';
import {
    matchWarningsToKeys,
    useDocWarnings,
    workflowKeyMatch
} from '../advisories';
import EvaluationStrip, {
    WarnBadge
} from './EvaluationStrip';
import {
    WORKFLOW_LEGEND
} from './evalLegends';
import FitPane from './FitPane';
import VizMarkerDefs from './VizMarkerDefs';
import VizTooltip, {
    useVizTooltip
} from './VizTooltip';
import WorkflowNode from './WorkflowNode';
import useCapabilityIndex from './useCapabilityIndex';
import './visuals.css';

interface DragState {
    key: string;
    sx: number;
    sy: number;
    bx: number;
    by: number;
}

type Offsets = Record<string, {
    dx: number;
    dy: number;
}>;

export default function WorkflowPipeline({
    docText
}: {
    docText: string;
}) {
    const {
        tenancy
    } = useAppStudio();
    const capIdx = useCapabilityIndex(tenancy);
    const tipApi = useVizTooltip();
    const [hover, setHover] = useState<string | null>(null);
    // View-only drag offsets per node key — NEVER written to the doc.
    const [offsets, setOffsets] = useState<Offsets>({});
    const dragRef = useRef<DragState | null>(null);
    const scaleRef = useRef(1);

    const parsed = useMemo(() => {
        try {
            return {
                doc: JSON.parse(docText),
                err: null as string | null
            };
        } catch (e: any) {
            return {
                doc: null,
                err: e?.message || 'invalid JSON'
            };
        }
    }, [docText]);

    const base = useMemo(() => {
        const doc = parsed.doc;
        if (!doc || !Array.isArray(doc.steps) || doc.steps.length === 0) {
            return null;
        }
        return buildWorkflowGraph(doc, (id) => capIdx?.get(id));
    }, [parsed.doc, capIdx]);

    // New document (or capability names arriving) → drop drag adjustments.
    useEffect(() => {
        setOffsets({});
    }, [base]);

    // Advisory overlay — debounced POST /validate/workflow on the doc.
    const {
        warnings,
        checking
    } = useDocWarnings('workflow', docText);
    const warnMap = useMemo(
        () => matchWarningsToKeys(warnings || [],
            base ? [...base.nodes.map((n) => n.key),
                ...base.frames.map((f) => f.key)] : [],
            workflowKeyMatch),
        [warnings, base]);

    const nodes = useMemo(() => {
        if (!base) return [];
        return base.nodes.map((n) => {
            const o = offsets[n.key];
            if (!o) return n;
            return {
                ...n,
                x: Math.max(2, n.x + o.dx),
                y: Math.max(2, n.y + o.dy)
            };
        });
    }, [base, offsets]);

    const byKey = useMemo(
        () => new Map(nodes.map((n) => [n.key, n])), [nodes]);
    const routed = useMemo(
        () => (base ? routeEdges(nodes, base.raw, 'v') : null), [base, nodes]);
    const frames = useMemo(() => {
        if (!base) return [];
        return base.frames
            .map((f) => ({
                f,
                r: frameRect(f, byKey)
            }))
            .filter((x) => x.r != null);
    }, [base, byKey]);

    useEffect(() => {
        if (base && base.problems.length) {
            // Surfaced inline too — this is the machine-readable trail.
            // eslint-disable-next-line no-console
            console.warn('[WorkflowPipeline] shapes not drawn:', base.problems);
        }
    }, [base]);

    if (parsed.err) {
        return (
            <div className="aps-empty" style={{
                padding: '40px 20px'
            }}>
                <span>Cannot draw — the JSON does not parse ({parsed.err}).
                    Fix it in the JSON view.</span>
            </div>
        );
    }
    if (!base || !routed) {
        return (
            <div className="aps-empty" style={{
                padding: '40px 20px'
            }}>
                <AlertTriangle size={18} />
                <span>No steps[] to draw in this workflow document.</span>
            </div>
        );
    }

    const canvasW = Math.max(routed.width,
        ...frames.map((x) => x.r!.x + x.r!.w + 24));
    const canvasH = Math.max(routed.height,
        ...frames.map((x) => x.r!.y + x.r!.h + 20));
    const isHl = (e: GEdge) => hover != null
        && (e.from === hover || e.to === hover || e.key === hover);
    const arrow = (e: GEdge) => {
        if (e.kind === 'comp') return 'url(#apsWfgArrComp)';
        if (e.kind === 'looprep') return 'url(#apsWfgArrLoop)';
        return isHl(e) ? 'url(#apsVizArrHl)' : 'url(#apsVizArr)';
    };

    const onNodeDown = (key: string) => (ev: React.PointerEvent<HTMLDivElement>) => {
        if (ev.button !== 0) return;
        const o = offsets[key] || {
            dx: 0,
            dy: 0
        };
        dragRef.current = {
            key,
            sx: ev.clientX,
            sy: ev.clientY,
            bx: o.dx,
            by: o.dy
        };
        ev.currentTarget.setPointerCapture(ev.pointerId);
        tipApi.hide();
    };
    const onNodeMove = (ev: React.PointerEvent<HTMLDivElement>) => {
        const d = dragRef.current;
        if (!d) return;
        const s = scaleRef.current || 1;
        setOffsets((prev) => ({
            ...prev,
            [d.key]: {
                dx: d.bx + (ev.clientX - d.sx) / s,
                dy: d.by + (ev.clientY - d.sy) / s
            }
        }));
    };
    const onNodeUp = () => {
        dragRef.current = null;
    };

    return (
        <div className="aps-eval-layout">
            <div className="aps-eval-main">
                {base.problems.length > 0 && (
                    <div className="aps-viz-problems">
                        <AlertTriangle size={14} />
                        {base.problems.length} shape{base.problems.length > 1 ? 's' : ''} could
                        not be drawn: {base.problems.join(' · ')}
                    </div>
                )}
                <FitPane>
                    {(scale) => {
                        scaleRef.current = scale;
                        return (
                            <div className="aps-viz-canvas" style={{
                                width: canvasW,
                                height: canvasH
                            }}>
                                {frames.map(({
                                    f,
                                    r
                                }) => (
                                    <div key={f.key} className="aps-wfg-frame" style={{
                                        left: r!.x,
                                        top: r!.y,
                                        width: r!.w,
                                        height: r!.h
                                    }}>
                                        <span
                                            className="aps-wfg-frame-label"
                                            onMouseEnter={(ev) => tipApi.show(ev, f.tipTitle, f.tipBody)}
                                            onMouseLeave={tipApi.hide}
                                        >
                                            <Repeat size={11} />
                                            {f.label}
                                        </span>
                                    </div>
                                ))}
                                <svg width={canvasW} height={canvasH} style={{
                                    position: 'absolute',
                                    inset: 0
                                }}>
                                    <VizMarkerDefs />
                                    {routed.edges.map((e) => (
                                        <path
                                            key={e.key}
                                            d={e.path}
                                            className={'aps-viz-edge ' + (e.kind || '')
                                                + (isHl(e) ? ' hl' : '')}
                                            markerEnd={arrow(e)}
                                        />
                                    ))}
                                </svg>
                                {routed.edges.filter((e) => e.label).map((e) => (
                                    <span
                                        key={e.key}
                                        className={'aps-viz-elabel ' + (e.kind || '')
                                            + (isHl(e) ? ' hl' : '')}
                                        style={{
                                            left: e.lx,
                                            top: e.ly
                                        }}
                                        onMouseEnter={(ev) => {
                                            setHover(e.key);
                                            tipApi.show(ev, e.tipTitle, e.tipBody);
                                        }}
                                        onMouseLeave={() => {
                                            setHover(null);
                                            tipApi.hide();
                                        }}
                                    >
                                        {e.label}
                                    </span>
                                ))}
                                {nodes.map((n) => (
                                    <WorkflowNode
                                        key={n.key}
                                        n={n}
                                        hl={hover === n.key}
                                        onDown={onNodeDown(n.key)}
                                        onMove={onNodeMove}
                                        onUp={onNodeUp}
                                        onEnter={(ev) => {
                                            setHover(n.key);
                                            tipApi.show(ev, n.tipTitle, n.tipBody);
                                        }}
                                        onLeave={() => {
                                            setHover(null);
                                            tipApi.hide();
                                        }}
                                    />
                                ))}
                                {nodes.filter((n) => warnMap.has(n.key)).map((n) => (
                                    <WarnBadge
                                        key={`warn:${n.key}`}
                                        x={n.x + n.w - 14}
                                        y={n.y - 9}
                                        items={warnMap.get(n.key) || []}
                                        onEnter={(ev) => tipApi.show(ev, 'Advisories',
                                            (warnMap.get(n.key) || []).join('\n\n'))}
                                        onLeave={tipApi.hide}
                                    />
                                ))}
                                {frames.filter(({
                                    f
                                }) => warnMap.has(f.key)).map(({
                                    f,
                                    r
                                }) => (
                                    <WarnBadge
                                        key={`warn:${f.key}`}
                                        x={r!.x + r!.w - 18}
                                        y={r!.y - 9}
                                        items={warnMap.get(f.key) || []}
                                        onEnter={(ev) => tipApi.show(ev, 'Advisories',
                                            (warnMap.get(f.key) || []).join('\n\n'))}
                                        onLeave={tipApi.hide}
                                    />
                                ))}
                            </div>
                        );
                    }}
                </FitPane>
                <VizTooltip tip={tipApi.tip} />
            </div>
            <EvaluationStrip
                warnings={warnings}
                checking={checking}
                legend={WORKFLOW_LEGEND}
            />
        </div>
    );
}
